package com.evoapps.evoshift.domain.model

import kotlinx.serialization.Serializable

/**
 * SH-107: the shared clock-in tablet of a workplace ("kiosk"). A paired tablet is signed in as a
 * device identity, not a person (functions/kiosk.js): it reads the workplace's shifts of today and
 * yesterday and its people's names and photos, and clocks people in and out after their PIN.
 */
data class KioskSession(
    val kioskId: String,
    val workplaceId: String,
    val workplaceName: String,
    /** The workplace's IANA time zone at pairing (e.g. "Europe/Warsaw"); null: the device's. */
    val timeZone: String?
)

/** A tablet in the workplace's list (workplaces/{id}/kiosks). */
data class KioskDevice(
    val id: String,
    val name: String,
    val createdBy: String,
    val createdAt: String,
    val lastSeenAt: String?,
    val revoked: Boolean
)

/** A one-time code a manager shows to pair a tablet; [expiresAt] is an ISO instant. */
data class KioskPairingCode(val code: String, val expiresAt: String)

/** What pairing returns. */
data class KioskPairing(val kioskId: String, val workplaceId: String, val workplaceName: String)

/** Someone the tablet can show: public profile only. */
data class KioskPerson(
    val userId: String,
    val displayName: String,
    val photoUrl: String?,
    /** May leave kiosk mode with their PIN. */
    val isManager: Boolean
)

/** Clock in or out; SH-116: start or end a break while clocked in. */
enum class ClockAction {
    IN, OUT, BREAK_START, BREAK_END;

    val isBreak: Boolean get() = this == BREAK_START || this == BREAK_END
}

/**
 * How a clock time came to be (SH-107 audit; QR: the tablet's code scanned in the app, SH-111;
 * SH-257 REPORTED: the person entered their own hours afterwards, the reportWorkHours function).
 */
enum class ClockSource { APP, KIOSK, MANAGER, QR, REPORTED }

/** Who wrote one end (start or end) of a shift's actual times, and how. */
data class ClockStamp(
    val source: ClockSource,
    val kioskId: String? = null,
    /** Queued offline on the tablet and sent later; the time is the device's. */
    val syncedLate: Boolean = false,
    /** The device's clock (ISO instant) of a queued event. */
    val deviceTime: String? = null,
    /** SH-106: the geofence check of this clock time (only the result, never the coordinates). */
    val locationCheck: LocationCheck? = null,
    /** SH-106: how far from the workplace, in metres, when a position was read. */
    val distanceMeters: Int? = null,
    /**
     * SH-180 ([ClockFlag.CLOCK_DRIFT]): how many minutes this self-reported time was ahead of
     * (positive) or behind the server's time when it was written, and that server time (ISO
     * instant). Written by the server only; null when the time was close or came from elsewhere.
     */
    val driftMinutes: Int? = null,
    val driftServerAt: String? = null
)

/**
 * The stamps of a shift's clock-in ([start]) and clock-out ([end]). SH-257: [reportedBy] and
 * [reportedAt] when the person entered their own hours (`actual.reportedBy` / `.reportedAt`,
 * written only by the reportWorkHours function).
 */
data class ClockAudit(
    val start: ClockStamp? = null,
    val end: ClockStamp? = null,
    val reportedBy: String? = null,
    val reportedAt: String? = null
) {
    /** SH-257: the times as they are now were entered by the person ("Zgłoszone przez pracownika"). */
    val reportedByWorker: Boolean
        get() = reportedBy != null && (start?.source == ClockSource.REPORTED || end?.source == ClockSource.REPORTED)

    val viaKiosk: Boolean get() = start?.source == ClockSource.KIOSK || end?.source == ClockSource.KIOSK
    val viaQr: Boolean get() = start?.source == ClockSource.QR || end?.source == ClockSource.QR
    val syncedLate: Boolean get() = start?.syncedLate == true || end?.syncedLate == true

    /** SH-106: the stamp a manager should look at: an OUTSIDE one first, else an UNVERIFIED one. */
    val locationFlag: ClockStamp?
        get() = listOfNotNull(start, end).firstOrNull { it.locationCheck == LocationCheck.OUTSIDE }
            ?: listOfNotNull(start, end).firstOrNull { it.locationCheck == LocationCheck.UNVERIFIED }
}

/**
 * One clock event from the tablet. [action] null: whatever the person's shift needs (the server
 * decides). [deviceTime] is when it was tapped (ISO instant); [queued] when it waited offline.
 * The PIN travels with it because only the server can check it; a queued event keeps it until
 * it is sent (see KioskClockQueue).
 */
@Serializable
data class KioskClockEvent(
    val eventId: String,
    val userId: String,
    val shiftId: String? = null,
    val action: ClockAction? = null,
    val pin: String,
    val deviceTime: String,
    val queued: Boolean = false
)

/**
 * A clock event done: [time] "HH:mm"; [queued] when it waits to be sent. SH-134: [unplanned] when
 * the person had no shift of their own (covering a colleague or a new shift, pending a manager);
 * [photoPath] where the tablet uploads the clock-in photo, when the workplace opted in.
 */
data class KioskClockResult(
    val action: ClockAction?,
    val time: String,
    val displayName: String,
    val queued: Boolean = false,
    val syncedLate: Boolean = false,
    val unplanned: UnplannedClockIn? = null,
    val flags: List<ClockFlag> = emptyList(),
    val photoPath: String? = null,
    /**
     * SH-124: after a clock-out, the person's own share of the day's tips in grosze when a
     * manager entered the day's pool (never anyone else's); null otherwise.
     */
    val tipShareMinor: Long? = null,
    val tipCurrency: String? = null,
    /** SH-127: the shift clocked (the server's answer; null when queued). */
    val shiftId: String? = null
)

/** SH-134: what the tablet must respect: no PIN pad ([qrOnly]); a photo after a PIN clock-in ([photo]). */
data class KioskSafeguards(val qrOnly: Boolean = false, val photo: Boolean = false)

/** SH-134: what an unplanned clock-in became: covering [coveredName]'s shift, or a new shift. */
data class UnplannedClockIn(val kind: UnplannedKind, val coveredName: String?)

/** The server refused a kiosk request; the facts, for the screen to word (UiText). */
class KioskException(
    val reason: Reason,
    val attemptsLeft: Int? = null,
    val retryAfterMinutes: Int? = null
) : Exception("Kiosk: $reason") {
    enum class Reason {
        WRONG_PIN, LOCKED, NO_PIN, PIN_SHAPE,
        /** SH-134: the workplace clocks in by QR code only (the tablet takes no PINs). */
        PIN_DISABLED,
        NO_SHIFT_NOW, ALREADY_DONE, NOT_CLOCKED_IN, NOT_YOUR_SHIFT, NOT_ON_TEAM,
        REVOKED, CODE_INVALID, PLAN_ENDED, NOT_A_MANAGER, BAD_DEVICE_TIME,
        /** SH-111: the scanned code is not one of ours, has expired, was used already, or QR is off. */
        QR_INVALID, QR_EXPIRED, QR_USED, QR_DISABLED,
        /** SH-116: a break is already going on / none is to end / the shift has the most breaks. */
        ALREADY_ON_BREAK, NOT_ON_BREAK, TOO_MANY_BREAKS,
        /** SH-127: a checklist task that needs a note got none / the checklist is not on the shift. */
        NOTE_REQUIRED, NO_CHECKLIST,
        UNKNOWN;

        companion object {
            fun of(name: String?): Reason = entries.find { it.name == name } ?: UNKNOWN
        }
    }
}

/** The request could not reach the server (no network): a clock event can be queued. */
class KioskOfflineException(cause: Throwable? = null) : Exception("Kiosk offline", cause)

/** How a person's tile on the tablet looks right now. */
enum class KioskTileState {
    CAN_CLOCK_IN, WORKING,
    /** SH-116: clocked in and on a break. */
    ON_BREAK,
    DONE, LATER
}

/** A person on shift today, as the tablet's grid shows them. */
data class KioskTile(
    val person: KioskPerson,
    val shift: Shift,
    val state: KioskTileState,
    /** "HH:mm" planned start / end. */
    val plannedStart: String,
    val plannedEnd: String,
    /** "HH:mm" clocked in, when working or done. */
    val clockedIn: String? = null,
    /** SH-116: "HH:mm" the break started, when on one. */
    val breakSince: String? = null
) {
    /** What the PIN pad's main button does for this person. */
    val action: ClockAction? get() = when (state) {
        KioskTileState.WORKING -> ClockAction.OUT
        KioskTileState.ON_BREAK -> ClockAction.BREAK_END
        KioskTileState.CAN_CLOCK_IN -> ClockAction.IN
        else -> null
    }

    /** SH-116: the PIN pad's second button: start a break while working, clock out while on one. */
    val otherAction: ClockAction? get() = when (state) {
        KioskTileState.WORKING -> ClockAction.BREAK_START
        KioskTileState.ON_BREAK -> ClockAction.OUT
        else -> null
    }
}

/**
 * SH-111: the rotating code the tablet shows. [token] is signed by the server (kioskQrToken) and
 * valid [ttlMillis] from when it was issued; the tablet asks for the next one before that.
 */
data class KioskQrCode(val token: String, val ttlMillis: Long) {
    /** When to fetch the next code: [REFRESH_MARGIN_MILLIS] before this one expires, at least 1 s. */
    val refreshAfterMillis: Long get() = (ttlMillis - REFRESH_MARGIN_MILLIS).coerceAtLeast(1_000)

    companion object {
        const val PREFIX = "evoshift-qr1."
        const val REFRESH_MARGIN_MILLIS = 5_000L

        /** Whether a scanned text looks like a tablet code (the server checks the signature). */
        fun looksLikeToken(text: String): Boolean = text.length in (PREFIX.length + 1)..512 && text.startsWith(PREFIX)
    }
}
